<template>
    <div class="data-card">
        <div class="data-card-toolbar">
            <span class="paginator-report">
                A ordem da lista é a <strong>prioridade de roteamento</strong> das transações.
            </span>
        </div>

        <DataTable class="data-table" lazy :value="configurations" dataKey="id" :autoLayout="true"
            responsiveLayout="scroll">

            <template #empty>
                <div class="data-empty">
                    <i class="pi pi-inbox"></i>
                    <span>Nenhum fornecedor registrado para este cliente.</span>
                    <Button class="p-button-text" type="button" icon="pi pi-plus" label="Registrar fornecedor"
                        @click="goToRegisterClient" />
                </div>
            </template>

            <Column field="id" header="ID" class="col-id">
                <template #body="slotProps">
                    <span class="record-id">{{ slotProps.data.id }}</span>
                </template>
            </Column>

            <Column header="Prioridade" class="col-id">
                <template #body="slotProps">
                    <span class="pill pill-muted">{{ slotProps.index + 1 }}º</span>
                </template>
            </Column>

            <Column field="vendors" header="Fornecedores">
                <template #body="slotProps">
                    <div class="pill-list">
                        <span v-for="vendor in slotProps.data.vendors" :key="vendor.id" class="pill pill-info"
                            :title="vendor.active ? vendor.name : `${vendor.name} (fornecedor inativo)`">
                            {{ vendor.name }}
                        </span>
                        <span v-if="!slotProps.data.vendors || !slotProps.data.vendors.length" class="empty-value">—</span>
                    </div>
                </template>
            </Column>

            <Column field="deposit_enabled" header="Depósito">
                <template #body="slotProps">
                    <span class="pill pill-status" :class="slotProps.data.deposit_enabled ? 'is-on' : 'is-off'">
                        <span class="pill-dot"></span>
                        {{ changeStatusBoleanToText(slotProps.data.deposit_enabled) }}
                    </span>

                    <div class="limit-list">
                        <span class="limit-label">Mínimo</span>
                        <span class="limit-value">{{ formatToBrl(slotProps.data.min_deposit_value) }}</span>
                        <span class="limit-label">Máximo</span>
                        <span class="limit-value">{{ formatToBrl(slotProps.data.max_deposit_value) }}</span>
                        <span class="limit-label">Limite diário</span>
                        <span class="limit-value">{{ formatToBrl(slotProps.data.daily_deposit_amount) }}</span>
                        <span class="limit-label">Qtd. por dia</span>
                        <span class="limit-value">{{ slotProps.data.daily_number_deposit }}</span>
                        <span class="limit-label">Limite diário por CPF</span>
                        <span class="limit-value">{{ formatToBrl(slotProps.data.daily_deposit_amount_per_cpf) }}</span>
                        <span class="limit-label">Qtd. diária por CPF</span>
                        <span class="limit-value">{{ slotProps.data.daily_number_deposit_per_cpf }}</span>
                    </div>
                </template>
            </Column>

            <Column field="withdraw_enabled" header="Saque">
                <template #body="slotProps">
                    <span class="pill pill-status" :class="slotProps.data.withdraw_enabled ? 'is-on' : 'is-off'">
                        <span class="pill-dot"></span>
                        {{ changeStatusBoleanToText(slotProps.data.withdraw_enabled) }}
                    </span>

                    <div class="limit-list">
                        <span class="limit-label">Mínimo</span>
                        <span class="limit-value">{{ formatToBrl(slotProps.data.min_withdraw_value) }}</span>
                        <span class="limit-label">Máximo</span>
                        <span class="limit-value">{{ formatToBrl(slotProps.data.max_withdraw_value) }}</span>
                        <span class="limit-label">Limite diário</span>
                        <span class="limit-value">{{ formatToBrl(slotProps.data.daily_withdraw_amount) }}</span>
                        <span class="limit-label">Qtd. por dia</span>
                        <span class="limit-value">{{ slotProps.data.daily_number_withdraw }}</span>
                        <span class="limit-label">Limite diário por CPF</span>
                        <span class="limit-value">{{ formatToBrl(slotProps.data.daily_withdraw_amount_per_cpf) }}</span>
                        <span class="limit-label">Qtd. diária por CPF</span>
                        <span class="limit-value">{{ slotProps.data.daily_number_withdraw_per_cpf }}</span>
                    </div>
                </template>
            </Column>

            <Column class="col-actions">
                <template #header>
                    Ações
                </template>
                <template #body="slotProps">
                    <div class="flex justify-content-end">
                        <SplitButton class="table-action-button" label="Editar" icon="pi pi-pencil"
                            :model="actionMenuItems(slotProps.data)" @click="openDetailsModalUpdate(slotProps.data)" />
                    </div>
                </template>
            </Column>

        </DataTable>
    </div>

    <Dialog class="data-dialog" v-model:visible="state.detailsModalUpdate" modal :style="{ width: '64rem' }"
        :header="`Editar configuração #${state.config ? state.config.id : ''}`">

        <ConfigurationForm embedded :configurationToUpate="state.config" :client="client" :isUpdanting="true"
            @closeUpdateModal="closeModal" />

    </Dialog>

    <Dialog class="data-dialog" v-model:visible="state.detailsModalClone" modal :style="{ width: '64rem' }"
        :header="`Clonar configuração #${state.config ? state.config.id : ''}`">

        <ConfigurationForm embedded :configurationToUpate="state.config" :client="client" :isCloning="true"
            @closeUpdateModal="closeModal" />

    </Dialog>

    <Dialog class="data-dialog" v-model:visible="state.priorityModalSelector" modal :style="{ width: '38rem' }"
        header="Priorizar fornecedores">

        <PriorityConfigurationsVendorSelector v-if="state.priorityModalSelector" :configurations="configurations"
            @closeUpdateModal="closeModal" />

    </Dialog>

    <Dialog class="data-dialog" v-model:visible="state.deleteModal" modal :style="{ width: '32rem' }"
        header="Excluir configuração">

        <div class="data-form-grid data-form-single">

            <div class="data-dialog-subject">
                <span class="pill pill-muted">ID {{ state.configurationToDelete ? state.configurationToDelete.id : '' }}</span>
                <span>{{ client.name }}</span>
            </div>

            <Message severity="error" sticky :closable="false">
                A exclusão é definitiva: o cliente deixa de rotear transações por esta configuração.
                Tem certeza que deseja excluir?
            </Message>

        </div>

        <template #footer>
            <Button class="p-button-text" type="button" label="Cancelar" @click="state.deleteModal = false" />
            <Button class="p-button-danger" :loading="state.loading.delete" type="button" icon="pi pi-trash"
                label="Excluir configuração" @click="tryToDelete" />
        </template>

    </Dialog>

</template>

<script>

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import SplitButton from 'primevue/splitbutton'
import Dialog from 'primevue/dialog'
import Message from 'primevue/message'
import ConfigurationForm from './ConfigurationForm.vue'
import PriorityConfigurationsVendorSelector from './PriorityConfigurationsVendorSelector.vue'
import { reactive } from 'vue'
import useNotificationToast from '../../../composables/useNotificationToast'
import { useStore } from 'vuex'
import services from '../../../services'

export default {

    inheritAttrs: false,

    components: { DataTable, Column, Button, SplitButton, Dialog, Message, ConfigurationForm, PriorityConfigurationsVendorSelector },

    props: {
        configurations: {
            type: Array
        },
        client: {
            type: Object
        }
    },

    emits: ['goToRegisterClient', 'realoadConfigurations'],

    setup(props, context) {

        const state = reactive({
            config: null,
            detailsModalUpdate: false,
            detailsModalClone: false,
            priorityModalSelector: false,
            deleteModal: false,
            configurationToDelete: null,
            loading: {
                delete: false
            }
        })

        const { success, error } = useNotificationToast()
        const store = useStore()

        const real = Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        })

        function changeStatusBoleanToText(active) {
            return active ? 'Ativado' : 'Desativado'
        }

        function formatToBrl(amount) {
            return real.format(amount || 0)
        }

        function actionMenuItems(config) {
            return [
                {
                    label: 'Clonar configuração',
                    icon: 'pi pi-clone',
                    command: () => openDetailsModalClone(config)
                },
                {
                    label: 'Priorizar fornecedores',
                    icon: 'pi pi-list',
                    command: () => openPriorityModalSelector()
                },
                {
                    label: 'Excluir configuração',
                    icon: 'pi pi-trash',
                    class: 'text-red-400',
                    command: () => openDeleteModal(config)
                },
            ]
        }

        function openDetailsModalUpdate(config) {
            state.config = config
            state.detailsModalUpdate = true
        }

        function openDetailsModalClone(config) {
            state.config = config
            state.detailsModalClone = true
        }

        function openPriorityModalSelector() {

            state.priorityModalSelector = true

        }

        function openDeleteModal(configurationToDelete) {

            state.configurationToDelete = configurationToDelete

            if (configurationToDelete.vendors.length > 0) {

                error("Limpe os fornecedores antes de deletar a configuração!")
                return
            }

            state.deleteModal = true

        }

        function goToRegisterClient() {
            context.emit('goToRegisterClient')
        }

        // o mesmo callback fecha os três modais de edição
        function closeModal() {
            state.detailsModalUpdate = false
            state.detailsModalClone = false
            state.priorityModalSelector = false
            context.emit('realoadConfigurations')
        }

        function tryToDelete() {

            const token = store.getters.getToken

            state.loading.delete = true

            services.configurations.delete({ configurationId: state.configurationToDelete.id, token })
                .then(() => {

                    success('Configuração deletada com sucesso!')

                    state.deleteModal = false

                    context.emit('realoadConfigurations')

                })
                .catch(() => {

                    error("Não foi possível deletar configurações.")

                })
                .finally(() => { state.loading.delete = false })
        }

        return {
            state,
            changeStatusBoleanToText,
            formatToBrl,
            actionMenuItems,
            openDetailsModalUpdate,
            openDetailsModalClone,
            openPriorityModalSelector,
            openDeleteModal,
            goToRegisterClient,
            closeModal,
            tryToDelete
        }

    }
}
</script>
